import { Component, AfterViewInit, ElementRef, OnDestroy, Renderer2, ViewChild, ViewEncapsulation, Input } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { GeneratorService } from 'src/app/deebr/service/generator.service';
import { DynamicSyncService, FirestoreSyncStatus } from 'src/app/deebr/service/dynamic-sync.service';


type ImageWithWeight = { src: string; weight: number | null };

@Component({
  selector: 'app-email-preview-popup',
  templateUrl: './email-preview-popup.component.html',
  styleUrls: ['./email-preview-popup.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false
})
export class EmailPreviewPopupComponent implements AfterViewInit, OnDestroy {
  @ViewChild('desktopContainer', { static: true }) desktopContainer!: ElementRef;
  @ViewChild('mobileContainer', { static: true }) mobileContainer!: ElementRef;
  private externalPreviewPopup!: HTMLElement;
  private iframeMobile!: HTMLIFrameElement;
  public hasAmpHtml: boolean = false; // Indicateur si AMPHTML est disponible
  public selectedView: "html" | "amphtml" = 'amphtml'; // Vue sélectionnée par défaut
  private styledHtml: string = ''; // Contenu HTML
  private styledAmpHtml: string = ''; // Contenu AMPHTML
  public spinnerinpreview = true;
  private destroy$: Subject<void> = new Subject();
  public userId!: number;
  public userName!: string;
  public firstName?: string;
  public lastName?: string;
  public objet?: string;
  public totalImagesWeightKB: number | null = null;
  showAltTooltip = false;
  showImagesWeightTooltip = false;
  public imagesWithWeight: ImageWithWeight[] = [];
  public previewUuid: string = '';
  public sampleUsers: { uuid: string; products: string[] }[] = [];
  public showUserDropdown = false;
  public sampleUsersLoaded = false;
  public isLoadingUsers = false;
  public isDarkMode: boolean = false;
  @Input() emailId?: number;
  @Input() isDynamic: boolean = false;
  public firestoreSyncStatus: FirestoreSyncStatus = 'ready';
  private syncSub?: Subscription;

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    private generatorService: GeneratorService,
    private syncService: DynamicSyncService) { }

  get isSyncing(): boolean {
    return this.firestoreSyncStatus === 'syncing';
  }

  get isSyncError(): boolean {
    return this.firestoreSyncStatus === 'error';
  }

  get previewDisabled(): boolean {
    return this.isSyncing || this.isSyncError;
  }

  get previewTooltip(): string {
    if (this.isSyncing) return 'Synchronisation en cours (~2 min) — aperçu utilisateur pas à jour';
    if (this.isSyncError) return 'Erreur de synchronisation, veuillez contacter votre chargé de compte';
    return '';
  }

  openPreviewPopup(html: string, ampHtml?: string, firstName?: string, lastName?: string, objet?: string) {
    console.log('Opening preview popup...');
    this.firstName = firstName
    this.lastName = lastName
    this.objet = objet
    this.styledHtml = html;
    this.styledAmpHtml = ampHtml || '';
    this.hasAmpHtml = !!ampHtml; // Vérifier si AMPHTML est disponible
    this.selectedView = this.hasAmpHtml ? 'amphtml' : 'html';
    this.calculateImagesWeight();

    if (this.hasAmpHtml) {
      this.showAmpHtml()
    }
    else {
      this.showHtml()
    }

    this.renderer.setStyle(this.externalPreviewPopup, 'visibility', 'visible');

  }
  ngAfterViewInit() {
    this.externalPreviewPopup = this.el.nativeElement.querySelector('#externalPreviewPopup');
    this.iframeMobile = this.el.nativeElement.querySelector('#iframeMobile');

    // Utilisez une fonction fléchée pour conserver le contexte de `this`

    this.renderer.listen(this.iframeMobile, 'load', () => this.onMobileIframeLoaded());

    if (this.isDynamic && this.emailId != null) {
      this.syncSub = this.syncService.getStatus$(this.emailId).subscribe(status => {
        this.firestoreSyncStatus = status;
      });
    }
  }

  ngOnDestroy(): void {
    this.syncSub?.unsubscribe();
  }

  get initials(): string {
    if (!this.firstName || !this.lastName) return '';
    return (
      (this.firstName[0] || '').toUpperCase() +
      (this.lastName[0] || '').toUpperCase()
    );
  }

  onDarkModeToggle(isChecked: boolean) {
    this.isDarkMode = isChecked;
    if (this.selectedView === 'amphtml' && this.hasAmpHtml) {
      this.showAmpHtml();
    } else {
      this.showHtml();
    }
  }

  /**
   * Injecte DarkReader dans l'iframe pour appliquer le même rendu dark
   * que Stripo dans son canvas (Stripo charge la même lib depuis ce CDN).
   */
  private forceDarkModePreview(html: string): string {
    const darkReaderInject = `<script src="https://plugins.stripo.email/resources/uieditor/latest/assets/darkreader/darkreader.js"></script>
<script>window.addEventListener('load',function(){if(window.DarkReader){window.DarkReader.enable({brightness:100,contrast:100,sepia:0});}});</script>`;
    if (/<\/head>/i.test(html)) {
      return html.replace(/<\/head>/i, darkReaderInject + '</head>');
    }
    return darkReaderInject + html;
  }

  close() {
    console.log('Closing preview popup...');
    this.isDarkMode = false;
    this.renderer.setStyle(this.externalPreviewPopup, 'visibility', 'hidden');

    // Réinitialiser le contenu des iframes pour éviter les conflits lors de la réouverture
    if (this.iframeMobile && this.iframeMobile.contentWindow) {
      console.log('Resetting iframeMobile content on close...');
      this.iframeMobile.contentWindow.document.open();
      this.iframeMobile.contentWindow.document.close();
    }

    const iframeDesktop = this.el.nativeElement.querySelector('#iframeDesktop');
    if (iframeDesktop && iframeDesktop.contentWindow) {
      console.log('Resetting iframeDesktop content on close...');
      iframeDesktop.contentWindow.document.open();
      iframeDesktop.contentWindow.document.close();
    }
  }





  onMobileIframeLoaded() {
    if (!this.iframeMobile || !this.iframeMobile.contentDocument) {
      console.warn('No contentDocument found for iframeMobile or iframeMobile is undefined');
      return;
    }

    const frameDocument = this.iframeMobile.contentDocument;

    this.renderer.setStyle(frameDocument.documentElement, 'overflow-x', 'visible');
    this.renderer.setStyle(frameDocument.body, 'overflow-y', 'scroll');
    this.renderer.setStyle(frameDocument.body, 'visibility', 'visible');

    const isAdaptive = !frameDocument.documentElement.querySelector('.gmail-fix');
    if (!isAdaptive) {
      const clientWidth = frameDocument.documentElement.clientWidth;
      const scrollWidth = frameDocument.documentElement.scrollWidth;
      const scale = (clientWidth / scrollWidth).toFixed(2);

      this.renderer.setStyle(frameDocument.documentElement, 'height', '100%');
      this.renderer.setStyle(frameDocument.documentElement, 'transform', `scale(${scale})`);
      this.renderer.setStyle(frameDocument.documentElement, 'transform-origin', 'top left');
      this.renderer.setStyle(frameDocument.body, 'overflow-x', 'hidden');
    }


  }




  updateSingleSelectGroupValue(selectedValue: any) {
    console.log('Selected View:', selectedValue);
    if (Array.isArray(selectedValue)) {
      selectedValue = selectedValue[0];  // Utiliser la première valeur si c'est un tableau
    }

    if (selectedValue === 'html') {
      console.log("coucou")
      this.showHtml();
    } else if (selectedValue === 'amphtml') {
      this.showAmpHtml();
    }
  }

  showHtml() {
    let html = this.injectUuid(this.styledHtml);
    if (this.isDarkMode) html = this.forceDarkModePreview(html);
    this.updateIframes(html);
  }

  showAmpHtml() {
    if (this.hasAmpHtml) {
      let html = this.injectUuid(this.styledAmpHtml);
      if (this.isDarkMode) html = this.forceDarkModePreview(html);
      this.updateIframes(html);
    }
  }

  /** Remplace le placeholder UUID dans les URLs CDN pour prévisualiser un user spécifique */
  private injectUuid(html: string): string {
    if (!this.previewUuid?.trim()) return html;
    // Remplace tout ce qui est entre /u/ et /slot/ dans les URLs cdn.deebr.co
    return html.replace(/(cdn\.deebr\.co\/u\/)([^\/]+)(\/slot\/)/g, `$1${this.previewUuid.trim()}$3`);
  }

  applyPreviewUuid() {
    this.showUserDropdown = false;
    if (this.selectedView === 'amphtml' && this.hasAmpHtml) {
      this.showAmpHtml();
    } else {
      this.showHtml();
    }
  }

  onUuidFocus() {
    if (!this.sampleUsersLoaded && this.emailId) {
      this.isLoadingUsers = true;
      this.showUserDropdown = true;
      this.generatorService.getSampleUsers(this.emailId).subscribe({
        next: (res: any) => {
          this.sampleUsers = res.users || [];
          this.sampleUsersLoaded = true;
          this.isLoadingUsers = false;
          this.showUserDropdown = this.sampleUsers.length > 0;
        },
        error: () => {
          this.sampleUsersLoaded = true;
          this.isLoadingUsers = false;
          this.showUserDropdown = false;
        }
      });
    } else if (this.sampleUsers.length > 0) {
      this.showUserDropdown = true;
    }
  }

  selectUser(uuid: string) {
    this.previewUuid = uuid;
    this.showUserDropdown = false;
    this.applyPreviewUuid();
  }

  onUuidBlur() {
    // Petit délai pour permettre le clic sur un item avant de fermer
    setTimeout(() => this.showUserDropdown = false, 200);
  }




  private updateIframes(content: string) {
    console.log('Updating iframes with content:', content.substring(0, 100));

    const iframeDesktop = this.el.nativeElement.querySelector('#iframeDesktop') as HTMLIFrameElement;
    if (iframeDesktop) {
      console.log('Setting srcdoc for iframeDesktop...');
      iframeDesktop.srcdoc = content;
    } else {
      console.warn('iframeDesktop not found');
    }

    if (this.iframeMobile) {
      console.log('Setting srcdoc for iframeMobile...');
      this.iframeMobile.srcdoc = content;
    } else {
      console.warn('iframeMobile not found');
    }
    this.spinnerinpreview = false;
  }


  //Calcul des indicateurs

  get htmlWeight(): number {
    if (!this.styledHtml) return 0;
    // Taille en octets (utf-8), puis conversion Ko
    return +(new TextEncoder().encode(this.styledHtml).length / 1024).toFixed(2);
  }
  get htmlWeightDisplay(): string {
    return `~${this.htmlWeight} KB`;
  }

  get isGmailTruncated(): boolean {
    return this.htmlWeight > 102; // limite Gmail
  }

  get textImageRatio(): string {
    if (!this.styledHtml) return '';
    // Texte "visible"
    const doc = new DOMParser().parseFromString(this.styledHtml, 'text/html');
    const allText = doc.body.innerText || '';
    const nbChars = allText.replace(/\s+/g, '').length;
    const nbImgs = doc.getElementsByTagName('img').length || 1;
    // Rapport = nombre de caractères par image
    const ratio = nbChars / nbImgs;

    // Interprétation (adapte à ta politique)
    if (ratio > 150) return 'Très bon';
    if (ratio > 60) return 'Bon';
    return 'Mauvais';
  }

  get altTextStatus(): string {
    if (!this.styledHtml) return '';
    const doc = new DOMParser().parseFromString(this.styledHtml, 'text/html');
    const imgs = Array.from(doc.getElementsByTagName('img'));
    const nbWithAlt = imgs.filter(img => img.hasAttribute('alt') && img.getAttribute('alt')!.trim() !== '').length;
    if (imgs.length === 0) return 'Aucune image';
    if (nbWithAlt === imgs.length) return 'OK';
    if (nbWithAlt === 0) return 'Manquant';
    return `${nbWithAlt}/${imgs.length} présents`;
  }
  isAbsoluteUrl(url: string): boolean {
    return /^https?:\/\//i.test(url);
  }

  get imgsMissingAlt(): string[] {
    if (!this.styledHtml) return [];
    const doc = new DOMParser().parseFromString(this.styledHtml, 'text/html');
    const imgs = Array.from(doc.getElementsByTagName('img'));
    return imgs
      .filter(img => !img.hasAttribute('alt') || img.getAttribute('alt')!.trim() === '')
      .map(img => img.getAttribute('src') || '');
  }


  get htmlWeightPercent(): number {
    const maxWeight = 60; // 60 Ko recommandé pour email
    return Math.min(100, (this.htmlWeight / maxWeight) * 100);
  }


  get imageUrls(): string[] {
    if (!this.styledHtml) return [];
    const doc = new DOMParser().parseFromString(this.styledHtml, 'text/html');
    return Array.from(doc.getElementsByTagName('img'))
      .map(img => img.getAttribute('src'))
      .filter(src => !!src) as string[];
  }

  // Type pour stocker infos image

  get htmlWeightTooltip(): string {
    return `Poids du HTML : ${this.htmlWeightDisplay}`;
  }



  async calculateImagesWeight() {
    this.totalImagesWeightKB = null;
    this.imagesWithWeight = [];

    const urls = this.imageUrls;
    if (!urls.length) {
      this.totalImagesWeightKB = 0;
      return;
    }
    let total = 0;
    const tmp: ImageWithWeight[] = [];
    for (const url of urls) {
      let weight: number | null = null;
      try {
        const response = await fetch(url, { method: 'HEAD' });
        const size = response.headers.get('content-length');
        if (size) {
          weight = parseInt(size, 10);
          total += weight;
        }
      } catch (e) {
        // ignore
      }
      tmp.push({ src: url, weight });
    }

    // Trie par poids décroissant (null à la fin)
    this.imagesWithWeight = tmp.sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));
    this.totalImagesWeightKB = +(total / 1024).toFixed(2);
  }

  get totalImagesWeightDisplay(): string | null {
    if (this.totalImagesWeightKB == null) return null;
    if (this.totalImagesWeightKB < 1000) {
      return `~${this.totalImagesWeightKB} KB`;
    }
    // Affiche en Mo, arrondi à 2 décimales
    return `~${(this.totalImagesWeightKB / 1024).toFixed(2)} Mo`;
  }




}
